import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';

import {
	AutoSuggest,
	IconButton,
	Layout,
	ListItem,
	Text,
} from '@phonectas/unifon-ui-kit';
import {debounce} from 'lodash';
import Highlighter from 'react-highlight-words';
import {ROUTES} from 'common/constants';
import {useNavigate} from 'react-router-dom';
import {isValidPhone} from 'utils/phoneNumber';
import {Accounts} from 'common/types/store/threeSixtyTypes';
import {DATE_FORMATS, dateByCustomFormat} from 'utils/date';
import {serviceStatusesColor} from 'utils/div';
import SearchIcon from 'common/components/icons/SearchIcon';
import {phoneNumber} from 'utils/number';
// import LargeSimCardIcon from 'common/components/icons/LargeSimCardIcon';

interface UserNotFoundProps {
	numb: string;
}

const UserNotFound: React.FC<UserNotFoundProps> = ({numb}) => {
	const [t] = useTranslation();
	const [value, setValue] = useState('');
	const {
		getAccountList,
		getUser,
	} = useThreeSixtyNewStore((state) => ({
		getAccountList: state.getAccountList,
		getUser: state.getUser,
	}), shallow);
	const navigate = useNavigate();
	const accountsList = useThreeSixtyNewStore((state) => state.accounts);
	const accounts = accountsList?.accounts;
	const debouncedFetchList = useMemo(() => debounce((searchValue) => {
		getAccountList(10, searchValue, 'serviceStatusAsc');
	}, 500), [getAccountList]);

	const handleChange = useCallback(
		 
		(val: string | number | string[] | number[] | null | undefined, _e: React.SyntheticEvent, _name: string) => {
			if (typeof val === 'string') {
				setValue(val);
				debouncedFetchList(val);
			} else if (typeof val === 'number') {
				setValue(val.toString());
				debouncedFetchList(val.toString());
			} else if (Array.isArray(val) && val.length > 0) {
				const first = val[0];
				if (typeof first === 'string') {
					setValue(first);
					debouncedFetchList(first);
				} else if (typeof first === 'number') {
					setValue(first.toString());
					debouncedFetchList(first.toString());
				}
			} else {
				setValue('');
			}
		},
		[debouncedFetchList]
	);

	const onClick = useCallback((item: string) => {
		if (isValidPhone(value)) {
			getUser({
				phone: value,
				accountId: item,
				onSuccess: () => {
					navigate(`${ROUTES.THREE_SIXTY}/${item}`);
				}
			});
		} else {
			navigate(`${ROUTES.THREE_SIXTY}/${item}`);
		}
	}, [navigate, getUser, value]);

	const renderOption = useCallback(
		({
			index,
			style,
			data: {collection},
		}: {
			index: number;
			style: React.CSSProperties;
			data: {collection: Accounts[]};
		}) => {
			if (!collection || !collection[index]) {
				return <div key={index}>No data</div>
			}
			const item = collection[index];
			const userPhone = item?.user_details?.phone_number;
			const userName = item?.user_details?.user_name;
			const isUser = item?.user_details?.is_user;
			const companyName = item?.name;
			const endDate = item?.user_details?.end_date;
			const status = item?.user_details?.service_status?.toString() || '';

			return (
				<ListItem
					key={index}
					value={item.id}
					onClick={() => onClick(item.id)}
					style={style}
					primaryText={
						<Layout nowrap fullWidth justifyContent="space-between">
							<Text variant="h5">
								<Highlighter
									autoEscape
									searchWords={[value]}
									textToHighlight={isUser ? userPhone : companyName ? companyName : ''}
								/>
							</Text>
							{
								endDate && (
									<Text>
										{dateByCustomFormat(endDate, DATE_FORMATS.DD_MM_YY)}
									</Text>
								)
							}
						</Layout>
					}
					secondaryText={
						<>
							{isUser ? (
								<Layout fullWidth justifyContent="space-between" nowrap>
									<Text color="textSecondary" variant="caption">
										{item.name && userName ? `${userName} - ${item.name}` : item.name}
									</Text>
									<Text
										style={{
											color: serviceStatusesColor(status).priColor,
										}}
										variant="caption"
									>
										{serviceStatusesColor(status).text}
									</Text>
								</Layout>
							) : (
								<Text color="textSecondary" variant="caption">
									{`${t('VAT')}: ${item.vat_number}`}
								</Text>
							)}
						</>
					}
				/>
			);
		},
		[value, onClick, t]
	);


	return (
		<Layout fullWidth justifyContent="center">
			<Layout style={{width: '40%'}}>
				<AutoSuggest
					placeholder={t('Search for customer')}
					fullWidth
					disableFiltering
					itemSize={60}
					onChange={handleChange}
					collection={accounts || []}
					renderItem={renderOption}
					endAdornment={
						<IconButton>
							<SearchIcon />
						</IconButton>
					}
					maxDropdownHeight={300}
				/>
			</Layout>
			<Layout justifyContent="center" fullWidth nowrap direction="row" style={{position: 'relative'}}>
				{/* <Layout style={{width: '80px', height: '43px'}}>
					<UnifonSimCardIcon bgColor='#dfdfdf' />
				</Layout> */}
				<Layout alignItems="center">
					<Text variant="h2">
						{t('User not found with number: {{numb}}', {numb: phoneNumber(numb)})}
					</Text>
				</Layout>
			</Layout>
			{/* <Layout justifyContent="center" fullWidth nowrap direction="row" style={{position: 'relative'}}>
				<div>
					<LargeSimCardIcon />
				</div>
			</Layout> */}
		</Layout>
	);
}

export default UserNotFound;
